import React, { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Globe2, Plus, Edit, Trash2, Loader2, RefreshCw, Search } from "lucide-react";
import type { Currency } from "@/types";

interface CountryItem {
  id: string | number;
  Id?: string | number;
  name?: string;
  CountryName?: string;
  code?: string;
  CountryCode?: string;
  currency_id?: string | number;
  currency?: any;
  status?: boolean;
}

export function CountryPage() {
  const [countries, setCountries] = useState<CountryItem[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState<CountryItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<CountryItem | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [form, setForm] = useState({ name: "", code: "", currency_id: "" });

  const { success, error } = useToast();

  const loadData = async () => {
    setLoading(true);
    try {
      const [countryRes, currRes] = await Promise.all([
        api.get("/api/Country/List"),
        api.get("/api/Currency/List"),
      ]);

      const countriesList = unwrapArray<CountryItem>(countryRes);
      const currenciesList = unwrapArray<Currency>(currRes);

      setCountries(countriesList);
      setCurrencies(currenciesList);
    } catch (err: any) {
      error("Fetch Error", err.message || "Failed to load countries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filtered = countries.filter((c) => {
    const name = (c.name || c.CountryName || "").toLowerCase();
    const code = (c.code || c.CountryCode || "").toLowerCase();
    const q = search.toLowerCase();
    return name.includes(q) || code.includes(q);
  });

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.code || !form.currency_id) {
      error("Validation Error", "All fields are required");
      return;
    }

    setActionLoading(true);
    try {
      await api.post("/api/Country/Insert", {
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        currency_id: form.currency_id,
      });
      success("Country Created", "New country created successfully");
      setAddOpen(false);
      setForm({ name: "", code: "", currency_id: "" });
      await loadData();
    } catch (err: any) {
      error("Creation Failed", err.message || "Failed to create country");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditOpen = (c: CountryItem) => {
    setEditItem(c);
    setForm({
      name: c.name || c.CountryName || "",
      code: c.code || c.CountryCode || "",
      currency_id: String(c.currency_id || ""),
    });
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editItem) return;

    setActionLoading(true);
    const id = editItem.id || editItem.Id;
    try {
      await api.put(`/api/Country/Update/${id}`, {
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        currency_id: form.currency_id,
      });
      success("Country Updated", "Country saved successfully");
      setEditItem(null);
      setForm({ name: "", code: "", currency_id: "" });
      await loadData();
    } catch (err: any) {
      error("Update Failed", err.message || "Failed to update country");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    setActionLoading(true);
    const id = deleteItem.id || deleteItem.Id;
    try {
      await api.delete(`/api/Country/Delete/${id}`);
      success("Country Deleted", "Country deleted successfully");
      setDeleteItem(null);
      await loadData();
    } catch (err: any) {
      error("Delete Failed", err.message || "Failed to delete country");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-foreground">
            Countries & Territories
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Maintain supported operating regions and linked default legal currencies
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
            className="h-9 gap-1.5"
          >
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setForm({ name: "", code: "", currency_id: "" });
              setAddOpen(true);
            }}
            className="h-9 gap-1.5 shadow-sm"
          >
            <Plus className="size-4" />
            Add Country
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
        <Input
          placeholder="Search countries by name or ISO code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-9 text-sm"
        />
      </div>

      {/* Table with Skeleton Loading */}
      {loading ? (
        <TableSkeleton rows={4} columns={4} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Country Name</TableHead>
                <TableHead>Country Code</TableHead>
                <TableHead>Linked Currency</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => {
                const name = c.name || c.CountryName || "Unnamed";
                const code = c.code || c.CountryCode || "N/A";
                const curr = currencies.find(
                  (cur: any) => String(cur.id || cur.Id) === String(c.currency_id)
                );

                return (
                  <TableRow key={c.id || c.Id}>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <div className="size-8 rounded-lg bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                          <Globe2 className="size-4" />
                        </div>
                        <span className="font-semibold text-sm text-foreground">
                          {name}
                        </span>
                      </div>
                    </TableCell>

                    <TableCell>
                      <code className="px-2 py-0.5 rounded bg-muted font-mono text-xs font-semibold">
                        {code}
                      </code>
                    </TableCell>

                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {curr
                          ? `${curr.CurrencyCode || (curr as any).code} — ${curr.CurrencyName || (curr as any).name}`
                          : (c.currency_id || "-")}
                      </span>
                    </TableCell>

                    <TableCell className="text-right space-x-1">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => handleEditOpen(c)}
                        title="Edit Country"
                      >
                        <Edit className="size-4 text-muted-foreground hover:text-foreground" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => setDeleteItem(c)}
                        title="Delete Country"
                      >
                        <Trash2 className="size-4 text-red-500 hover:text-red-700" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-10 text-muted-foreground text-sm">
                    No countries configured.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Dialog: Add */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold">Add Country</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Register a new country or territory
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddSubmit} className="space-y-4 sm:space-y-5 py-2">
            <div className="space-y-2">
              <Label>Country Name *</Label>
              <Input
                required
                placeholder="Singapore"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2">
                <Label>Country Code *</Label>
                <Input
                  required
                  placeholder="SG"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className="font-mono uppercase"
                />
              </div>
              <div className="space-y-2">
                <Label>Linked Currency *</Label>
                <Select
                  value={form.currency_id}
                  onValueChange={(val) => setForm({ ...form, currency_id: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((cur: any) => (
                      <SelectItem key={cur.id || cur.Id} value={String(cur.id || cur.Id)}>
                        {cur.code || cur.CurrencyCode} - {cur.name || cur.CurrencyName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save Country
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Edit */}
      <Dialog open={!!editItem} onOpenChange={(open) => !open && setEditItem(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold">Edit Country</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Update country name, code, or linked currency
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 sm:space-y-5 py-2">
            <div className="space-y-2">
              <Label>Country Name *</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2">
                <Label>Country Code *</Label>
                <Input
                  required
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className="font-mono uppercase"
                />
              </div>
              <div className="space-y-2">
                <Label>Linked Currency *</Label>
                <Select
                  value={form.currency_id}
                  onValueChange={(val) => setForm({ ...form, currency_id: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((cur: any) => (
                      <SelectItem key={cur.id || cur.Id} value={String(cur.id || cur.Id)}>
                        {cur.code || cur.CurrencyCode} - {cur.name || cur.CurrencyName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setEditItem(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Delete */}
      <Dialog open={!!deleteItem} onOpenChange={(open) => !open && setDeleteItem(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg text-red-600 dark:text-red-400">Delete Country</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Are you sure you want to remove{" "}
              <strong className="text-foreground">
                {deleteItem?.name || deleteItem?.CountryName} ({deleteItem?.code || deleteItem?.CountryCode})
              </strong>
              ?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" onClick={() => setDeleteItem(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={actionLoading}
            >
              {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
              Confirm Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
